/**
 * W1-11b (app-shell-ui): snap controls in the top bar (DESIGN.md §4.4, §8.2): beat snap (1/1 ... 1/32 with the
 * triplet divisions marked, any custom divisor up to 1/1920), the beat-snap magnet, the X grid line count and the
 * X-snap magnet. Pickers are popovers with preset buttons and a number field; magnets are toggle buttons bound to
 * `snap.toggleBeat` / `snap.toggleX` (Shift+G / Shift+X).
 */

import { useState, type SyntheticEvent } from 'react';
import { Button } from '../../ui/Button';
import { IconButton } from '../../ui/IconButton';
import { Popover } from '../../ui/Popover';
import { SNAP_DIV_MAX, SNAP_DIV_MIN, X_GRID_MAX, X_GRID_MIN } from '../../state/viewState';
import type { AppServices } from '../registry/types';
import { SNAP_PRESETS } from './playback';
import { useEditorState, useText, type ShellText } from './ShellContext';
import { m } from './shell.messages';
import s from './Shell.module.css';

/** X grid presets (RPE "vertical lines"; 11 is the default). */
export const X_GRID_PRESETS: readonly number[] = [3, 5, 7, 9, 11, 13, 17, 21, 25, 33];

const TRIPLETS = new Set([3, 6, 12, 24]);

export function snapLabel(div: number, text: ShellText): string {
  return text.t(m.snapDivValue, { div });
}

function NumberForm(props: {
  readonly label: string;
  readonly value: number;
  readonly min: number;
  readonly max: number;
  readonly onApply: (v: number) => void;
  readonly text: ShellText;
}) {
  const [draft, setDraft] = useState(String(props.value));
  const n = Number(draft);
  const valid = Number.isInteger(n) && n >= props.min && n <= props.max;
  const submit = (e: SyntheticEvent): void => {
    e.preventDefault();
    if (valid) props.onApply(n);
  };
  return (
    <form className={s.snapForm} onSubmit={submit}>
      <label className={s.snapFormLabel}>
        <span>{props.label}</span>
        <input
          type="number"
          inputMode="numeric"
          min={props.min}
          max={props.max}
          step={1}
          value={draft}
          aria-invalid={valid ? undefined : true}
          onChange={(e) => {
            setDraft(e.target.value);
          }}
        />
      </label>
      <Button
        type="submit"
        size="sm"
        variant="primary"
        disabledReason={valid ? null : { key: m.paletteArgNumber, params: { min: props.min, max: props.max } }}
      >
        {props.text.t(m.snapApply)}
      </Button>
    </form>
  );
}

export function SnapControls(props: { readonly services: AppServices }) {
  const { services } = props;
  const text = useText(services);
  const snap = useEditorState(services, (st) => st.snap);
  const [divOpen, setDivOpen] = useState(false);
  const [xOpen, setXOpen] = useState(false);
  const setSnap = (p: Parameters<ReturnType<AppServices['editor']['getState']>['setSnap']>[0]): void => {
    services.editor.getState().setSnap(p);
  };

  return (
    <div className={s.snap} role="group" aria-label={text.t(m.snapLabel)}>
      <span className={s.snapCaption}>{text.t(m.snapLabel)}</span>
      <Popover
        open={divOpen}
        onOpenChange={setDivOpen}
        label={text.t(m.snapDivLabel)}
        align="start"
        trigger={
          <button
            type="button"
            className={s.pickerButton}
            aria-label={text.t(m.labelValue, { label: text.t(m.snapDivLabel), value: snapLabel(snap.div, text) })}
          >
            {snapLabel(snap.div, text)}
          </button>
        }
      >
        <div className={s.presetGrid} role="group" aria-label={text.t(m.snapDivLabel)}>
          {SNAP_PRESETS.map((d) => (
            <button
              key={d}
              type="button"
              className={s.presetButton}
              aria-pressed={snap.div === d}
              title={TRIPLETS.has(d) ? text.t(m.snapDivTriplet, { div: d }) : undefined}
              onClick={() => {
                setSnap({ div: d });
                setDivOpen(false);
              }}
            >
              {snapLabel(d, text)}
            </button>
          ))}
        </div>
        <NumberForm
          key={snap.div}
          label={text.t(m.snapCustomLabel)}
          value={snap.div}
          min={SNAP_DIV_MIN}
          max={SNAP_DIV_MAX}
          text={text}
          onApply={(div) => {
            setSnap({ div });
            setDivOpen(false);
          }}
        />
      </Popover>
      <IconButton
        icon="Magnet"
        label={{ key: m.cmdSnapBeat }}
        command="snap.toggleBeat"
        pressed={snap.beatSnap}
        size="sm"
      />
      <Popover
        open={xOpen}
        onOpenChange={setXOpen}
        label={text.t(m.xGridLabel)}
        align="start"
        trigger={
          <button
            type="button"
            className={s.pickerButton}
            aria-label={text.t(m.labelValue, { label: text.t(m.xGridLabel), value: String(snap.xGrid) })}
          >
            {text.t(m.xGridValue, { n: snap.xGrid })}
          </button>
        }
      >
        <div className={s.presetGrid} role="group" aria-label={text.t(m.xGridLabel)}>
          {X_GRID_PRESETS.map((n) => (
            <button
              key={n}
              type="button"
              className={s.presetButton}
              aria-pressed={snap.xGrid === n}
              onClick={() => {
                setSnap({ xGrid: n });
                setXOpen(false);
              }}
            >
              {n}
            </button>
          ))}
        </div>
        <NumberForm
          key={snap.xGrid}
          label={text.t(m.xGridLabel)}
          value={snap.xGrid}
          min={X_GRID_MIN}
          max={X_GRID_MAX}
          text={text}
          onApply={(xGrid) => {
            setSnap({ xGrid });
            setXOpen(false);
          }}
        />
      </Popover>
      <IconButton icon="Magnet" label={{ key: m.cmdSnapX }} command="snap.toggleX" pressed={snap.xSnap} size="sm" />
    </div>
  );
}
